<script>

import * as turf from '@turf/turf'
import mapboxgl from 'mapbox-gl';

// Import plugins
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';

// Import styles
import 'mapbox-gl/dist/mapbox-gl.css'
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css'

export default {
  name: 'MapBoxDisplay',
  props: {
    sources: {
      required: false,
      type: Array,
      default: function () {
        return null;
      },
    },
    token: {
      required: true,
      type: String,
    },
    render: {
      required: false,
      default: true,
      type: Boolean,
    },
  },
  data() {
    return {
      mapStyle: 'mapbox://styles/mapbox/satellite-streets-v11',
      map: null,
      geography: null,
      mapDraw: {},
      sourceIds: [],
      loaded: false,
      center: [-93.627594, 42.025494],
      identifier: 'map-' + Math.floor(10000 * Math.random()),
      fullBounds: new mapboxgl.LngLatBounds(),
    };
  },
  watch: {
    sources(newVal) {
      if (newVal) {

        if (this.sourceIds.length > 0) {
          this.clearMap()
        }

        if (this.map === null) {
          this.renderMap()
        } else {
          if (this.loaded) {
            this.postMapRender()
          }
        }
      }
    },
  },
  created() {
    this.processSources(this.sources)
  },
  mounted() {
    if (this.render) {
      this.renderMap()
    }
  },
  methods: {
    /**
     * Initiates creating the map and loading the data.
     */
    renderMap: function () {
      this.createMap();
      this.addControls();

      this.map.on('load', function () {
        this.loaded = true
        this.map.resize()
        this.postMapRender()
      }.bind(this));
    },

    /**
     * Creates map with added plugins.
     */
    createMap() {
      mapboxgl.accessToken = this.token;

      this.map = new mapboxgl.Map({
        container: this.identifier,
        style: this.mapStyle, // stylesheet location
        center: this.center,
        zoom: 3, // starting zoom
        transformRequest: (url, resourceType) => {
          if (resourceType === 'Source' && url.startsWith('https://gis.apfo.usda.gov')) {
            return {
              url: url,
              headers: { 'Access-Control-Allow-Origin': true }
            }
          }
        }
      });

      this.map.scrollZoom.disable();
    },

    /**
     * Reads an inputted geography
     */
    processSources(geography) {
      if (geography) {
        this.geography = turf.getGeom(geography)
      } else {
        this.geography = null
        this.center = [-93.627594, 42.025494]
      }
    },

    /**
     * Adds data to map and sets bounds.
     */
    postMapRender() {
      this.addGeography();
      this.setMapBoundsToFit();
    },

    /**
     * Adds map controls
     */
    addControls() {
      if (this.draw) {
        this.map.addControl(new MapboxGeocoder({
          accessToken: mapboxgl.accessToken,
          mapboxgl: mapboxgl
        }), 'top-right');
      }

      // Add zoom and rotation controls to the map.
      this.map.addControl(new mapboxgl.NavigationControl());
    },

    /**
     * Add geography layer on the map.
     */
    addGeography() {
      this.sources.forEach((source,index) => {
        console.log('source')
        console.log(source)
        this.map.addSource(source.id, {
          'type': 'geojson',
          'data': turf.getGeom(source.geography),
        })

        this.sourceIds.push(source.id)

        this.map.addLayer({
          'id': source.id + '-phill',
          'type': 'fill', // options :'fill', "line", "symbol", "circle", "heatmap", "fill-extrusion", "raster", "hillshade", "background", "sky"... SET paint accordingly
          'source': source.id,
          'paint': {
            'fill-color': source.color,
            'fill-opacity': 0.3
          }
        });

        this.map.addLayer({
          'id': source.id + '-outline',
          'type': 'line', // options :'fill', "line", "symbol", "circle", "heatmap", "fill-extrusion", "raster", "hillshade", "background", "sky"... SET paint accordingly
          'source': source.id,
          'paint': {
            'line-color': source.color,
            'line-width': 3,
            'line-dasharray': index ? [3,5] : [5,3]
          }
        });
      });
    },

    /**
     * Remove all geography layers on the map.
     */
    clearMap() {
      if (this.map) {
        this.sourceIds.forEach((sourceId) => {
          if (this.map.getLayer(sourceId + '-phill')) {
            this.map.removeLayer(sourceId + '-phill');
          }

          if (this.map.getLayer(sourceId + "-outline")) {
            this.map.removeLayer(sourceId + "-outline");
          }

          if (this.map.getSource(sourceId)) {
            this.map.removeSource(sourceId);
          }
        });
      }
    },

    /**
     * Sets the bounds of a map to fit the given geography.
     */
    setMapBoundsToFit() {
      let featureCollection = {
        features: this.sources.map(a => a.geography),
        type: 'FeatureCollection'
      }

      let geography = turf.getGeom(featureCollection)

      this.fullBounds = new mapboxgl.LngLatBounds();
      let bbox = turf.bbox(geography);

      this.fullBounds.extend([bbox[0], bbox[1]]);
      this.fullBounds.extend([bbox[2], bbox[3]]);

      this.map.fitBounds(this.fullBounds, {
        padding: 30,
        duration: 0,
      });
    },
  },
};

</script>

<template>
  <div class="w-full">
    <div :id="identifier" class="h-128 w-full"></div>
  </div>
</template>
